import { TextField, TextFieldProps } from "@mui/material";
import * as React from "react";
import { round } from "../../utils/lodash-replacements";
import { parseLocaleNumber } from "../../utils/numberParsing";

type Unit = "meters" | "feet";

interface Props extends Omit<TextFieldProps, "onChange" | "value" | "onBlur"> {
  value: number; // Value stored in feet
  decimals?: number;
  unit: Unit; // Display unit: meters or feet
  onChange: (valueInFeet: number) => void; // Callback for value change in feet
  onBlur?: (valueInFeet: number) => void; // Callback for value change in feet
}

export const MetricAwareTextField: React.FC<Props> = ({
  value,
  decimals: decimalsProvided,
  unit,
  onChange,
  onBlur,
  ...textFieldProps
}) => {
  const defaultDecimals = unit === "meters" ? 2 : 1;
  const decimals = decimalsProvided ?? defaultDecimals;

  // Conversion factors
  const METERS_TO_FEET = 3.28084;

  // Local state for tracking user input during editing
  const [inputValue, setInputValue] = React.useState<string>("");
  const [isFocused, setIsFocused] = React.useState(false);

  // Compute display value from prop
  const displayValue = round(
    unit === "meters" ? value / METERS_TO_FEET : value,
    decimals // Use calculated precision (meters=2, feet=1)
  );

  // Sync local input state when parent value changes externally (only when not focused)
  React.useEffect(() => {
    if (!isFocused) {
      setInputValue(displayValue.toFixed(decimals));
    }
  }, [displayValue, isFocused, decimals]);

  // Handle input changes - just update local state while typing
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  // Handle focus - initialize input value for editing
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    setInputValue(displayValue.toFixed(decimals));
    textFieldProps.onFocus?.(event);
  };

  // Handle blur - parse input and update parent
  const handleBlur = (_event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);

    const parsedValue = parseLocaleNumber(inputValue);
    const conversion = unit === "meters" ? METERS_TO_FEET : 1;
    const valueInFeet = parsedValue * conversion;

    // Update parent with parsed value
    onChange(valueInFeet);

    // Call parent's onBlur if provided
    if (onBlur) {
      onBlur(valueInFeet);
    }
  };

  // Step size matches decimal precision
  const step = decimals === 0 ? 1 : Math.pow(10, -decimals);

  // Handle arrow key increment/decrement
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault(); // Prevent default browser behavior

      // Calculate new display value
      const delta = event.key === 'ArrowUp' ? step : -step;
      const newDisplayValue = Math.max(0, displayValue + delta); // Don't go below 0

      // Update local input state for immediate visual feedback
      setInputValue(newDisplayValue.toFixed(decimals));

      // Convert to feet and call onChange
      const conversion = unit === "meters" ? METERS_TO_FEET : 1;
      const valueInFeet = newDisplayValue * conversion;

      onChange(valueInFeet);
    }
  };

  // Extract and convert min/max constraints from parent
  // Parent provides values in feet (internal storage unit), but HTML validation
  // needs them in display units to match displayValue
  const parentInputProps = textFieldProps.InputProps?.inputProps || {};

  const convertConstraint = (valueInFeet: number | undefined): number | undefined => {
    if (valueInFeet === undefined) return undefined;
    return unit === "meters" ? round(valueInFeet / METERS_TO_FEET, 2) : valueInFeet;
  };

  const convertedMin = convertConstraint(parentInputProps.min) ?? 0;
  const convertedMax = convertConstraint(parentInputProps.max);

  return (
    <TextField
      {...textFieldProps}
      type="text" // Use text input for locale-aware decimal handling
      onChange={handleInputChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      value={isFocused ? inputValue : displayValue.toFixed(decimals)}
      InputProps={{
        ...textFieldProps.InputProps,
        endAdornment: unit === "meters" ? "m" : "ft",
        inputProps: {
          ...parentInputProps,
          inputMode: "decimal", // Shows numeric keyboard on mobile
          pattern: "[0-9.,\\-]*", // Allow numbers, comma, period, and minus
          step,
          min: convertedMin,
          ...(convertedMax !== undefined && { max: convertedMax }),
        },
      }}
    />
  );
};
